// src/components/LeanFlowSimulator.jsx
// Simulador de Flujo Lean - UI Component
// Ticket 2: UI: Página de selección por combos + panel dinámico

import React, { useState, useEffect } from 'react';
import { leanFlowEngine } from '../services/leanFlowEngine';
import examplesData from '../data/Simulador/ejemplos_lean.json';
import { 
  Lock, 
  Unlock, 
  ChevronRight, 
  CheckCircle, 
  AlertCircle, 
  RotateCcw,
  AlertTriangle,
  Target,
  Lightbulb,
  BarChart3,
  XCircle,
  Shield,
  ChevronDown,
  ChevronUp,
  BookOpen,
  MessageSquare
} from 'lucide-react';

const STORAGE_KEY = 'leanFlowDraft.v1';
const MAX_STEPS = 6;

// ============================================================================
// SUB-COMPONENTE: FlowStepSelect
// ============================================================================
function FlowStepSelect({ 
  stepIndex, 
  isEnabled, 
  isLocked, 
  selectedStageId, 
  options, 
  onSelect, 
  onLock,
  hasError 
}) {
  const handleSelectChange = (e) => {
    onSelect(stepIndex, e.target.value);
  };

  const handleLockClick = () => {
    if (!selectedStageId) return;
    onLock(stepIndex);
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      {/* Select Dropdown */}
      <div className="relative">
        <select
          id={`step-${stepIndex}`}
          disabled={!isEnabled || isLocked}
          value={selectedStageId || ''}
          onChange={handleSelectChange}
          className={`
            w-full px-4 py-3 rounded-lg border text-sm font-medium
            transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-offset-1
            ${hasError ? 'border-red-400 ring-2 ring-red-200' : ''}
            ${isLocked 
              ? 'bg-green-50 border-green-500 text-green-900 cursor-not-allowed' 
              : isEnabled
                ? 'bg-white border-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-blue-300 cursor-pointer'
                : 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
          aria-label={`Seleccionar estado para paso ${stepIndex + 1}`}
        >
          <option value="">Seleccionar...</option>
          {options.map(opt => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* Badge numérico */}
        <div className={`
          absolute -top-2 -left-2 w-7 h-7 rounded-full 
          flex items-center justify-center text-sm font-bold shadow-sm
          ${isLocked 
            ? 'bg-green-600 text-white' 
            : isEnabled 
              ? 'bg-blue-600 text-white'
              : 'bg-gray-400 text-white'
          }
        `}>
          {stepIndex + 1}
        </div>

        {/* Badge de confirmado - Solo cuando está locked */}
        {isLocked && (
          <div className="absolute -top-2 -right-2 bg-green-600 rounded-full p-1 shadow-sm" title="Confirmado">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        )}
      </div>

      {/* Botones de acción - Solo mostrar cuando NO está locked */}
      {isEnabled && !isLocked && (
        <div className="flex gap-2">
          <button
            onClick={handleLockClick}
            disabled={!selectedStageId}
            className={`
              flex-1 inline-flex items-center justify-center gap-1.5 
              px-3 py-2 rounded-md text-xs font-semibold
              transition-all duration-200
              ${selectedStageId
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }
            `}
            aria-label="Fijar selección actual"
          >
            <Lock className="w-3 h-3" />
            Fijar
          </button>
        </div>
      )}

      {/* Error mensaje */}
      {hasError && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Debes seleccionar una opción antes de fijar
        </p>
      )}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTE: FlowStepsRow
// ============================================================================
function FlowStepsRow({ flowState, onSelect, onLock, errorStep }) {
  const steps = Array.from({ length: MAX_STEPS }, (_, idx) => {
    const stepInfo = leanFlowEngine.getStepInfo(idx);
    return { ...stepInfo, index: idx };
  });

  return (
    <section className="bg-white sm:rounded-xl sm:border border-gray-200 sm:shadow-sm p-0 sm:p-6 lg:p-8" aria-labelledby="validation-route-title">
      <h2 id="validation-route-title" className="text-base font-bold text-[#0c203b] mb-4 md:mb-6 flex items-center gap-2.5 px-3 sm:px-0">
        <Target className="w-6 h-6 text-blue-600" aria-hidden="true" />
        Ruta de validación
      </h2>

      {/* Grid de combos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-6 px-3 sm:px-0">
        {steps.map((step, idx) => (
          <div key={idx} className={`${
            flowState.currentStepIndex === idx && step.selection
              ? 'ring-2 ring-[#0c203b] ring-offset-2 rounded-xl'
              : ''
          }`}>
            <FlowStepSelect
              stepIndex={step.index}
              isEnabled={step.enabled}
              isLocked={step.locked}
              selectedStageId={step.selection?.stageId || null}
              options={step.options}
              onSelect={onSelect}
              onLock={onLock}
              hasError={errorStep === idx}
            />
          </div>
        ))}
      </div>

      {/* Leyenda */}
      <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200 flex flex-wrap gap-3 sm:gap-4 text-xs text-gray-600 px-3 sm:px-0">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center">
            <span className="text-white text-[10px] font-bold">✓</span>
          </div>
          <span>Confirmado</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-blue-600 rounded-full"></div>
          <span>Habilitado</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-gray-400 rounded-full"></div>
          <span>Deshabilitado</span>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// SUB-COMPONENTE: LockedRouteSummary (Zona C)
// ============================================================================
function LockedRouteSummary({ completedPath }) {
  if (completedPath.length === 0) return null;

  const stages = completedPath.map(stageId => 
    leanFlowEngine.getStageContent(stageId)
  );

  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <CheckCircle className="w-4 h-4 text-green-600" />
        Ruta confirmada ({completedPath.length}/{MAX_STEPS})
      </h3>
      
      <div className="flex flex-wrap gap-2">
        {stages.map((stage, idx) => (
          <React.Fragment key={stage.id}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-blue-200 text-sm font-medium text-gray-700 shadow-sm">
              <span className="text-xs text-gray-500">#{idx + 1}</span>
              {stage.label}
            </div>
            {idx < stages.length - 1 && (
              <ChevronRight className="w-4 h-4 text-gray-400 self-center" />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTE: ExamplesSection (Ejemplos colapsables)
// ============================================================================
function ExamplesSection({ title, examples }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!examples || examples.length === 0) return null;

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all border border-gray-200 group"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-gray-600 group-hover:text-gray-800" />
          <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">Ver ejemplos reales</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-500 group-hover:text-gray-700" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500 group-hover:text-gray-700" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-2.5">
          {examples.map((item, idx) => (
            <div
              key={idx}
              className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:bg-white hover:border-gray-300 transition-all"
            >
              <div className="flex items-center gap-2 mb-2.5">
                <span className="px-2 py-0.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded">
                  {item.domain}
                </span>
              </div>
              <p className="text-sm text-gray-900 font-medium mb-2 leading-relaxed">
                {item.example}
              </p>
              {item.note && (
                <p className="text-xs text-gray-600 leading-relaxed">
                  {item.note}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTE: StageDetailPanel (Zona B)
// ============================================================================
function StageDetailPanel({ panel, stepIndex, isConfirmed }) {
  if (!panel) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
        <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <h3 className="text-xl font-bold text-gray-800 mb-2">
          Selecciona un estado para comenzar
        </h3>
        <p className="text-gray-600">
          Elige una opción en el Paso 1 para ver las recomendaciones específicas de validación
        </p>
      </div>
    );
  }

  const content = panel.content;
  
  // Obtener ejemplos para este stage
  const stageExamples = examplesData.stages[panel.stageId] || {};

  const sections = [
    {
      title: 'Qué debes tener para partir',
      icon: CheckCircle,
      color: 'default',
      items: content.whatYouHave || [],
      examplesKey: 'startRequirements'
    },
    {
      title: 'Qué debes validar ahora',
      icon: Target,
      color: 'default',
      items: content.whatToValidate || []
    },
    {
      title: 'Experimentos recomendados',
      icon: Lightbulb,
      color: 'default',
      items: content.experiments || [],
      isExperiment: true,
      examplesKey: 'experiments'
    },
    {
      title: 'Métricas/umbrales sugeridos',
      icon: BarChart3,
      color: 'default',
      items: [content.threshold],
      isThreshold: true,
      examplesKey: 'metrics'
    },
    {
      title: 'Qué NO medir todavía',
      icon: XCircle,
      color: 'alert',
      items: content.whatNotToMeasure || [],
      examplesKey: 'antiPatterns'
    },
    {
      title: 'Riesgo típico en esta etapa',
      icon: AlertTriangle,
      color: 'warning',
      items: [content.typicalRisk],
      isRisk: true
    }
  ];

  const colorClasses = {
    default: { bg: 'bg-slate-50', text: 'text-slate-900', icon: 'text-[#0c203b]', dot: 'bg-slate-600' },
    alert: { bg: 'bg-red-50', text: 'text-slate-900', icon: 'text-red-600', dot: 'bg-slate-600' },
    warning: { bg: 'bg-amber-50', text: 'text-slate-900', icon: 'text-amber-700', dot: 'bg-slate-600' }
  };

  return (
    <div className="bg-white sm:rounded-xl sm:border border-gray-200 sm:shadow-sm p-0 sm:p-6 lg:p-8">
      {/* Header */}
      <header className="border-b border-gray-200 pb-4 mb-4 md:pb-6 md:mb-6 px-3 sm:px-0">
        <div className="flex items-center gap-3 mb-3">
          <div className={`px-3 py-1.5 text-white text-sm font-bold rounded-md flex items-center gap-2 ${
            isConfirmed ? 'bg-green-600' : 'bg-[#0c203b]'
          }`}>
            {isConfirmed && <CheckCircle className="w-4 h-4" />}
            Paso {stepIndex + 1} de 6
          </div>
          <div className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-md">
            {panel.label}
          </div>
        </div>
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#0c203b] mb-3">
          {content.title}
        </h2>
        <p className="text-gray-600 text-sm lg:text-base mb-4">{panel.description}</p>
      </header>

      {/* Secciones en grid */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3 lg:gap-4 px-3 sm:px-0" aria-label="Guías de validación Lean">
        {sections.map((section, idx) => {
          const colors = colorClasses[section.color];
          const Icon = section.icon;

          return (
            <article 
              key={idx}
              className={`${colors.bg} rounded-lg p-2.5 sm:p-3 lg:p-4 border border-${section.color}-200`}
              aria-labelledby={`section-${idx}-title`}
            >
              {/* Header con jerarquía visual mejorada */}
              <div className="flex items-start justify-between mb-2 pb-2 border-b border-gray-200">
                <h3 id={`section-${idx}-title`} className={`text-base sm:text-lg font-bold ${colors.text} flex items-center gap-2 flex-1 leading-tight`}>
                  <Icon className={`w-5 h-5 ${colors.icon} flex-shrink-0`} aria-hidden="true" />
                  <span>{section.title}</span>
                </h3>
                <span className="text-[10px] sm:text-xs font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 px-2 py-1 rounded-md flex-shrink-0 ml-2" aria-label={`Orden de lectura: ${idx + 1}`}>
                  <span className="sm:hidden">{idx + 1}°</span>
                  <span className="hidden sm:inline">
                    {idx === 0 ? 'Primero' : idx === 1 ? 'Segundo' : idx === 2 ? 'Tercero' : idx === 3 ? 'Cuarto' : idx === 4 ? 'Quinto' : 'Sexto'}
                  </span>
                </span>
              </div>

              {/* Contenido */}
              <div>
              {section.isThreshold || section.isRisk ? (
                <ul className="space-y-1.5">
                  <li className={`text-xs sm:text-sm ${colors.text} flex gap-2 leading-relaxed`}>
                    <span className={`${colors.dot} w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0`}></span>
                    <span>{section.items[0] || 'Sin contenido definido'}</span>
                  </li>
                </ul>
              ) : section.isExperiment ? (
                /* Renderizado especial para experimentos con {name, notes} */
                <div className="space-y-2.5">
                  {section.items.length > 0 ? (
                    section.items.map((exp, itemIdx) => (
                      <div 
                        key={itemIdx} 
                        className={`bg-white/60 rounded-md p-2.5 border border-${section.color}-200`}
                      >
                        <p className={`text-xs sm:text-sm font-semibold ${colors.text} mb-1`}>
                          {exp.name || exp}
                        </p>
                        {exp.notes && (
                          <p className={`text-xs ${colors.text} opacity-75 leading-relaxed`}>
                            {exp.notes}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className={`text-xs ${colors.text} opacity-60`}>Sin experimentos definidos</p>
                  )}
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {section.items.length > 0 ? (
                    section.items.map((item, itemIdx) => (
                      <li key={itemIdx} className={`text-xs sm:text-sm ${colors.text} flex gap-2 leading-relaxed`}>
                        <span className={`${colors.dot} w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0`}></span>
                        <span>{item}</span>
                      </li>
                    ))
                  ) : (
                    <li className={`text-xs ${colors.text} opacity-60`}>Sin contenido definido</li>
                  )}
                </ul>
              )}
              </div>
              
              {/* Ejemplos reales colapsables */}
              {section.examplesKey && stageExamples[section.examplesKey] && Array.isArray(stageExamples[section.examplesKey]) && (
                <ExamplesSection 
                  title={section.title}
                  examples={stageExamples[section.examplesKey].reduce((acc, group) => {
                    if (group.examples && Array.isArray(group.examples)) {
                      return [...acc, ...group.examples];
                    }
                    return acc;
                  }, [])}
                />
              )}
            </article>
          );
        })}
      </section>
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL: LeanFlowSimulator
// ============================================================================
export default function LeanFlowSimulator() {
  const [flowState, setFlowState] = useState(leanFlowEngine.getState());
  const [errorStep, setErrorStep] = useState(null);

  // Cargar estado desde localStorage al montar
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        leanFlowEngine.importState(saved);
        setFlowState(leanFlowEngine.getState());
      } catch (error) {
        console.error('Error cargando estado guardado:', error);
      }
    }
  }, []);

  // Guardar estado en localStorage cada vez que cambia
  useEffect(() => {
    const stateJson = leanFlowEngine.exportState();
    localStorage.setItem(STORAGE_KEY, stateJson);
  }, [flowState]);

  const handleSelectStage = (stepIndex, stageId) => {
    if (!stageId) return;
    
    setErrorStep(null);
    const newState = leanFlowEngine.setSelection(stepIndex, stageId);
    setFlowState(newState);
  };

  const handleLockStep = (stepIndex) => {
    const stepInfo = leanFlowEngine.getStepInfo(stepIndex);
    
    if (!stepInfo.selection) {
      setErrorStep(stepIndex);
      setTimeout(() => setErrorStep(null), 3000);
      return;
    }

    const newState = leanFlowEngine.lockStep(stepIndex, stepInfo.selection.stageId);
    setFlowState(newState);
    setErrorStep(null);
  };

  const handleReset = () => {
    if (confirm('¿Estás seguro de que deseas reiniciar todo el flujo? Se perderá el progreso actual.')) {
      const newState = leanFlowEngine.reset();
      setFlowState(newState);
      setErrorStep(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 space-y-6 md:space-y-10">
      
      {/* Instrucciones de uso - PRIMERO para dar contexto */}
      <div className="space-y-2 sm:space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 sm:p-3 md:p-4 flex items-start gap-2">
          <BookOpen className="w-5 h-5 text-[#0c203b] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-slate-700 leading-relaxed">
              <span className="font-semibold text-[#0c203b]">Metodología Lean Startup paso a paso:</span> Estas tarjetas siguen un orden lógico (1→6) para validar tu startup correctamente. Te indican los requisitos previos, criterios de validación, experimentos MVP, métricas clave, antipatrones comunes y riesgos típicos.
            </p>
          </div>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 sm:p-3 md:p-4 flex items-start gap-2">
          <Target className="w-5 h-5 text-green-700 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-slate-700 leading-relaxed">
              <span className="font-semibold text-green-800">Criterio de éxito para avanzar:</span> Confirma este paso solo cuando tus experimentos de validación <span className="font-semibold">superen el umbral mínimo viable</span> definido en la tarjeta 4 (métricas). Ese indicador confirma la hipótesis y te permite avanzar con confianza.
            </p>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm">
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Progreso</p>
              <p className="text-xs sm:text-sm font-bold text-gray-800">
                {flowState.completedPath.length} / {MAX_STEPS} pasos
              </p>
            </div>
          </div>
          
          {flowState.isComplete && (
            <div className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-semibold border border-green-300">
              <CheckCircle className="w-5 h-5" />
              ¡Flujo completado!
            </div>
          )}
        </div>

        <button
          onClick={handleReset}
          className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 text-xs sm:text-sm font-semibold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all"
          aria-label="Reiniciar flujo completo"
        >
          <RotateCcw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">Reiniciar</span>
        </button>
      </div>

      {/* Zona A: Combos Flow */}
      <FlowStepsRow 
        flowState={flowState}
        onSelect={handleSelectStage}
        onLock={handleLockStep}
        errorStep={errorStep}
      />

      {/* Zona B: Panel de Detalle */}
      <StageDetailPanel 
        panel={flowState.currentPanel} 
        stepIndex={flowState.selections.length - 1}
        isConfirmed={
          flowState.selections.length > 0 && 
          flowState.selections[flowState.selections.length - 1]?.locked === true
        }
      />

      {/* CTA Final */}
      {flowState.isComplete && (
        <div className="bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 rounded-xl border border-green-300 p-5 sm:p-6 lg:p-8 text-center shadow-md">
          <Shield className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-green-600" />
          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#0c203b] mb-2">
            ¡Felicidades! Has completado la validación Lean
          </h3>
          <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 max-w-2xl mx-auto">
            Tu startup ha recorrido todos los estados de validación. Ahora tienes una ruta clara 
            de experimentos y métricas para seguir construyendo con confianza.
          </p>
          <a
            href="mailto:informes@edutech.pe?subject=Completé la Validación Lean&body=Hola, he completado el simulador de validación Lean y me gustaría compartir mi experiencia:%0D%0A%0D%0A"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm sm:text-base font-bold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
          >
            <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
            Quiero dejar un comentario
          </a>
        </div>
      )}

      {/* Botón de Comentario - Solo visible cuando NO está completo */}
      {!flowState.isComplete && (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 text-center">
        <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4">
          ¿Tienes sugerencias o comentarios sobre este simulador?
        </p>
        <a
          href="mailto:informes@edutech.pe?subject=Comentario sobre Simulador de Validación Lean&body=Hola, me gustaría compartir mi feedback sobre el simulador:%0D%0A%0D%0A"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#0c203b] text-white font-semibold rounded-lg hover:bg-[#1a365d] transition-all shadow-sm hover:shadow-md"
        >
          <MessageSquare className="w-4 h-4" />
          Quiero dejar un comentario
        </a>
      </div>
      )}
    </div>
  );
}
